import React, { useState, useEffect } from 'react';
import { storage, db } from '../../firebase/config';
import { ref, uploadBytes, getDownloadURL, listAll } from 'firebase/storage';
import { collection, addDoc, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { Upload, File, Download, Shield, Loader } from 'lucide-react';

interface FileVaultProps {
  targetUserId?: string; // If lawyer viewing client's vault
}

const FileVault: React.FC<FileVaultProps> = ({ targetUserId }) => {
  const { user, role, assignedLawyerId } = useAuth();
  const [files, setFiles] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  // Determine whose files to show
  // If Client: show my own files and those shared with me
  // If Lawyer: show my own files and those shared with me by my assigned client
  const effectiveUserId = targetUserId || user?.uid;

  useEffect(() => {
    fetchFiles();
  }, [effectiveUserId]);

  const fetchFiles = async () => {
    if (!effectiveUserId) return;
    setLoading(true);
    const q = query(
      collection(db, 'files'),
      where('visibleTo', 'array-contains', user?.uid)
    );
    const snap = await getDocs(q);
    setFiles(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    setLoading(false);
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    setUploading(true);
    try {
      const storageRef = ref(storage, `vault/${user.uid}/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      // Visibility Logic
      let visibleTo = [user.uid];
      if (role === 'client' && assignedLawyerId) {
        visibleTo.push(assignedLawyerId);
      } else if (role === 'lawyer' && targetUserId) {
        visibleTo.push(targetUserId);
      }
      // Admins always get added via security rules/backend, but for simplicity here:
      // In a real app, you'd fetch all admins and add them or handle via rules.

      await addDoc(collection(db, 'files'), {
        fileName: file.name,
        fileUrl: url,
        ownerId: user.uid,
        ownerRole: role,
        visibleTo: visibleTo,
        createdAt: new Date().toISOString()
      });

      alert('File uploaded to secure vault.');
      fetchFiles();
    } catch (err) {
      console.error(err);
      alert('Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: 'var(--shadow)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Shield size={20} color="var(--primary-color)" /> Secure File Vault
        </h3>
        <label htmlFor="file-vault-upload" className="btn btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
          {uploading ? <Loader className="spin" size={18} /> : <Upload size={18} />}
          {uploading ? 'Uploading...' : 'Upload Document'}
          <input id="file-vault-upload" name="vaultUpload" type="file" onChange={handleUpload} style={{ display: 'none' }} disabled={uploading} />
        </label>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>Scanning vault...</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {files.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#999', border: '2px dashed #eee', borderRadius: '8px' }}>
              No documents found in this vault.
            </div>
          ) : (
            files.map(file => (
              <div key={file.id} style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                padding: '15px', 
                border: '1px solid #eee', 
                borderRadius: '8px',
                transition: 'var(--transition)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ backgroundColor: '#f0f0f0', padding: '8px', borderRadius: '4px' }}>
                    <File size={20} color="#666" />
                  </div>
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '0.9rem' }}>{file.fileName}</div>
                    <div style={{ fontSize: '0.75rem', color: '#999' }}>Uploaded by {file.ownerRole} • {new Date(file.createdAt).toLocaleDateString()}</div>
                  </div>
                </div>
                <a href={file.fileUrl} target="_blank" rel="noreferrer" className="btn" style={{ padding: '8px', backgroundColor: '#f5f5f5' }}>
                  <Download size={18} color="var(--primary-color)" />
                </a>
              </div>
            ))
          )}
        </div>
      )}

      <style>{`
        .spin { animation: rotate 2s linear infinite; }
        @keyframes rotate { 100% { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default FileVault;
